import React, { useEffect, useLayoutEffect, useState } from 'react';
import { VIDRO } from './NoBoard';

/**
 * TOUR GUIADO (13/10, pedido do dono: "faz um tour guiado na tela, deixa um
 * botão de interrogação que ele vai mostrando o que é cada coisa... as telas vão
 * abrindo e a pessoa entendendo como operar, como analisar").
 *
 * Cada passo aponta um elemento da tela pelo atributo `data-tour` e pode rodar
 * uma ação ANTES de aparecer (abrir o foco de um fluxo, abrir o popup de uma
 * etapa) — é assim que "as telas vão abrindo". O passo sem alvo, ou cujo alvo
 * não está na tela, vira um cartão central: o tour nunca trava por falta de
 * elemento.
 */
export interface PassoDoTour {
  /** Valor do `data-tour` do elemento a destacar. Ausente = cartão central. */
  alvo?: string;
  titulo: string;
  texto: string;
  /** Roda antes de o passo aparecer (abre/fecha telas). */
  antes?: () => void;
}

/** id do encaixe, no cabeçalho da página, onde o botão "?" do tour é desenhado. */
export const SLOT_DO_TOUR = 'orq-slot-tour';

interface TourGuiadoProps {
  passos: PassoDoTour[];
  onClose: () => void;
}

interface Caixa {
  top: number;
  left: number;
  width: number;
  height: number;
}

const FOLGA = 8;
const LARGURA_BALAO = 380;

export const TourGuiado: React.FC<TourGuiadoProps> = ({ passos, onClose }) => {
  const [indice, setIndice] = useState(0);
  const [caixa, setCaixa] = useState<Caixa | null>(null);
  const passo = passos[indice];

  // A ação roda primeiro; a medição espera a tela que ela abriu terminar de montar.
  useEffect(() => {
    if (!passo) return undefined;
    passo.antes?.();
    setCaixa(null);
    const medir = () => {
      const el = passo.alvo
        ? document.querySelector<HTMLElement>(`[data-tour="${passo.alvo}"]`)
        : null;
      if (!el) {
        setCaixa(null);
        return;
      }
      el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'auto' });
      const r = el.getBoundingClientRect();
      setCaixa({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const t1 = window.setTimeout(medir, 380);
    // Segunda medição: listas longas ainda estão assentando na primeira.
    const t2 = window.setTimeout(medir, 900);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indice]);

  useLayoutEffect(() => {
    const aoTeclar = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
      if (ev.key === 'ArrowRight') setIndice(i => Math.min(passos.length - 1, i + 1));
      if (ev.key === 'ArrowLeft') setIndice(i => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [onClose, passos.length]);

  if (!passo) return null;

  const alturaTela = window.innerHeight;
  const larguraTela = window.innerWidth;
  // Balão abaixo do alvo quando cabe; senão acima; sem alvo, no centro.
  let estiloBalao: React.CSSProperties = {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };
  if (caixa) {
    const cabeAbaixo = caixa.top + caixa.height + 230 < alturaTela;
    const cabeAcima = caixa.top > 230;
    const left = Math.max(
      12,
      Math.min(larguraTela - LARGURA_BALAO - 12, caixa.left + caixa.width / 2 - LARGURA_BALAO / 2),
    );
    if (cabeAbaixo) estiloBalao = { top: caixa.top + caixa.height + FOLGA + 10, left };
    else if (cabeAcima) estiloBalao = { bottom: alturaTela - caixa.top + FOLGA + 10, left };
    else estiloBalao = { bottom: 20, left };
  }
  const ultimo = indice === passos.length - 1;

  return (
    <>
      {/* Escurece tudo MENOS o alvo: a sombra gigante do recorte faz o papel do
          overlay, e `pointerEvents: none` deixa a tela por baixo intacta. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          zIndex: 90,
          pointerEvents: 'none',
          borderRadius: '16px',
          transition: 'all 0.25s ease',
          boxShadow: '0 0 0 9999px oklch(0.2 0.02 165 / 0.55)',
          ...(caixa
            ? {
                top: caixa.top - FOLGA,
                left: caixa.left - FOLGA,
                width: caixa.width + FOLGA * 2,
                height: caixa.height + FOLGA * 2,
                border: '2px solid oklch(0.78 0.14 162)',
              }
            : { top: '50%', left: '50%', width: 0, height: 0 }),
        }}
      />
      <div
        role="dialog"
        aria-label={`Tour guiado: ${passo.titulo}`}
        style={{
          ...VIDRO,
          background: 'rgba(255,255,255,0.97)',
          position: 'fixed',
          zIndex: 91,
          width: `min(${LARGURA_BALAO}px, calc(100vw - 24px))`,
          boxSizing: 'border-box',
          borderRadius: '18px',
          padding: '18px 18px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          ...estiloBalao,
        }}
      >
        <div
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '10.5px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'oklch(0.5 0.08 165)',
          }}
        >
          Passo {indice + 1} de {passos.length}
        </div>
        <div style={{ fontSize: '16px', fontWeight: 700, color: 'oklch(0.25 0.04 165)' }}>
          {passo.titulo}
        </div>
        <div
          style={{
            fontSize: '13px',
            lineHeight: 1.5,
            color: 'oklch(0.38 0.02 165)',
            textWrap: 'pretty',
          }}
        >
          {passo.texto}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
          <button type="button" onClick={onClose} style={BOTAO_SECUNDARIO}>
            Sair
          </button>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => setIndice(i => Math.max(0, i - 1))}
            disabled={indice === 0}
            style={{ ...BOTAO_SECUNDARIO, opacity: indice === 0 ? 0.4 : 1 }}
          >
            ← Anterior
          </button>
          <button
            type="button"
            onClick={() => (ultimo ? onClose() : setIndice(i => i + 1))}
            style={BOTAO_PRIMARIO}
          >
            {ultimo ? 'Concluir' : 'Próximo →'}
          </button>
        </div>
      </div>
    </>
  );
};

const BOTAO_SECUNDARIO: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '10px',
  border: '1px solid oklch(0.9 0.008 160)',
  background: 'oklch(1 0 0)',
  font: 'inherit',
  fontSize: '12.5px',
  fontWeight: 600,
  color: 'oklch(0.4 0.02 165)',
  cursor: 'pointer',
};

const BOTAO_PRIMARIO: React.CSSProperties = {
  ...BOTAO_SECUNDARIO,
  border: '1px solid oklch(0.22 0.026 168)',
  background: 'oklch(0.22 0.026 168)',
  color: 'oklch(0.96 0.01 160)',
};
